import React from 'react';
import {
  BJJ_POSITIONS,
  getSecondaryNames,
  getTertiariesFor,
} from '../../data/combatives';

/**
 * PositionPicker — 3-level cascading dropdown for BJJ technique position.
 *
 * Value shape: { primary, secondary, tertiary } — all strings, secondary
 * and tertiary may be empty.
 *
 * Uses native <select> elements for reliable mobile keyboard / picker UX.
 * Secondary appears only after Primary is set; Tertiary appears only when
 * the picked Secondary has tertiary options.
 */
const PositionPicker = ({ value = {}, onChange, testId = 'position-picker' }) => {
  const { primary = '', secondary = '', tertiary = '' } = value;

  const secondaryNames = primary ? getSecondaryNames(primary) : [];
  const tertiaryNames = primary && secondary ? getTertiariesFor(primary, secondary) : [];

  const setPrimary = (p) => onChange({ primary: p, secondary: '', tertiary: '' });
  const setSecondary = (s) => onChange({ primary, secondary: s, tertiary: '' });
  const setTertiary = (t) => onChange({ primary, secondary, tertiary: t });

  const selectClass = 'w-full px-2 py-1.5 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <div>
        <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Primary</label>
        <select
          value={primary}
          onChange={(e) => setPrimary(e.target.value)}
          data-testid={`${testId}-primary`}
          className={selectClass}
        >
          <option value="">— pick —</option>
          {BJJ_POSITIONS.map((p) => (
            <option key={p.primary} value={p.primary}>{p.label || p.primary}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Sub-position</label>
        <select
          value={secondary}
          onChange={(e) => setSecondary(e.target.value)}
          disabled={!primary || secondaryNames.length === 0}
          data-testid={`${testId}-secondary`}
          className={`${selectClass} disabled:opacity-40`}
        >
          <option value="">— pick —</option>
          {secondaryNames.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Specific</label>
        <select
          value={tertiary}
          onChange={(e) => setTertiary(e.target.value)}
          disabled={!secondary || tertiaryNames.length === 0}
          data-testid={`${testId}-tertiary`}
          className={`${selectClass} disabled:opacity-40`}
        >
          <option value="">{tertiaryNames.length === 0 ? '—' : '— pick —'}</option>
          {tertiaryNames.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default PositionPicker;
